'use client'

import React, { useCallback, useState } from 'react'
import Link from 'next/link'
import { track } from '@/utils/analytics'
import AppStoreLink from '@/app/components/AppStoreLink'
import SubscribeForm from '@/app/components/newsletter/SubscribeForm'

/**
 * Live barcode lookup UI.
 *
 * Kept deliberately thin for INP, which is the Core Web Vital most sites still
 * fail: no work happens on keystroke at all. The input is uncontrolled-in-
 * spirit — typing only sets local state, and the single network call fires on
 * submit. There is no debounce loop, no per-character filtering, and no
 * re-render of a long list while you type.
 */

interface Band {
  label: string
  hex: string
  hexDark: string
  guidance: string
}

interface LookupResponse {
  ok: true
  barcode: string
  source: 'open_food_facts' | 'open_beauty_facts'
  product: { name: string; brand?: string; quantity?: string; image?: string }
  estimate: {
    score: number
    band: Band
    grade: 'C' | 'D'
    gradeMeaning: string
    confidence: number
    material: { label: string; materialSlug?: string; isPlastic: boolean } | null
    drivers: string[]
    unknown: string
  }
  context: {
    novaGroup?: number
    novaLabel?: string
    nutriScore?: string
    ecoScore?: string
    additivesCount?: number
    hasAny: boolean
  }
  curated?: { slug: string; name: string; score: number; grade: string }
}

type State =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'result'; data: LookupResponse }
  | { phase: 'miss'; reason: string; barcode: string }

export default function BarcodeLookup() {
  const [value, setValue] = useState('')
  const [state, setState] = useState<State>({ phase: 'idle' })

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const barcode = value.replace(/\D/g, '')
      if (barcode.length < 8) {
        setState({ phase: 'miss', reason: 'invalid_barcode', barcode: value })
        return
      }

      setState({ phase: 'loading' })
      try {
        const res = await fetch(`/api/product-lookup?barcode=${encodeURIComponent(barcode)}`)
        const data = await res.json()

        if (!data.ok) {
          setState({ phase: 'miss', reason: data.reason ?? 'not_found', barcode })
          track('product_search_miss', {
            cta_surface: 'barcode_lookup',
            page_type: 'barcode_check',
            search_query: barcode,
          })
          return
        }

        setState({ phase: 'result', data })
        track('product_search', {
          cta_surface: 'barcode_lookup',
          page_type: 'barcode_check',
          search_query: barcode,
          result_count: 1,
        })
      } catch {
        setState({ phase: 'miss', reason: 'network', barcode })
      }
    },
    [value]
  )

  return (
    <div>
      <form onSubmit={submit} className="rounded-2xl border border-forest/25 bg-white p-5 shadow-sm sm:p-6">
        <label htmlFor="barcode" className="block text-xs font-semibold uppercase tracking-[0.2em] text-forest">
          Enter a barcode
        </label>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row">
          <input
            id="barcode"
            name="barcode"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. 5449000000996"
            className="min-w-0 flex-1 rounded-xl border border-ink/15 bg-cream/50 px-4 py-3.5 text-ink tabular-nums placeholder:text-gray-400 focus:border-forest focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-forest/40"
          />
          <button
            type="submit"
            disabled={state.phase === 'loading'}
            className="shrink-0 rounded-control bg-ink px-6 py-3.5 font-medium text-cream transition-colors hover:bg-ink/90 disabled:opacity-60 pressable"
          >
            {state.phase === 'loading' ? 'Checking…' : 'Check it'}
          </button>
        </div>
        <p className="mt-2.5 text-sm text-gray-500">
          The 8–14 digit number under the bars. Works on food, drink, and
          cosmetics. No account, nothing installed.
        </p>
      </form>

      <div aria-live="polite">
        {state.phase === 'loading' && (
          <p className="mt-6 text-center text-sm text-gray-500">Looking it up…</p>
        )}

        {state.phase === 'miss' && <Miss reason={state.reason} barcode={state.barcode} />}

        {state.phase === 'result' && <Result data={state.data} />}
      </div>
    </div>
  )
}

function Miss({ reason, barcode }: { reason: string; barcode: string }) {
  const copy: Record<string, { title: string; body: string }> = {
    invalid_barcode: {
      title: 'That is not a barcode',
      body: 'Barcodes are 8 to 14 digits, printed under the bars. Letters and spaces are fine to leave in — we strip them.',
    },
    network: {
      title: 'Open Food Facts did not answer',
      body: 'This one is on the connection, not on the product. Nothing is wrong with the item — try again in a moment.',
    },
    not_found: {
      title: `Nothing recorded for ${barcode}`,
      body: 'Open Food Facts is crowdsourced, so coverage is uneven and plenty of real products are simply missing. That is not a verdict on the product.',
    },
  }
  const c = copy[reason] ?? copy.not_found

  return (
    <div className="mt-6 rounded-2xl border border-forest/20 bg-gradient-to-br from-forest/10 to-forest/[0.03] p-6 text-center">
      <h2 className="font-display text-xl font-bold text-ink">{c.title}</h2>
      <p className="mx-auto mt-2 max-w-md leading-relaxed text-gray-700">{c.body}</p>
      {reason === 'not_found' && (
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-gray-600">
          The app does not depend on a barcode being listed — it identifies the
          product and its packaging visually, which also works for cookware,
          drinkware and anything else with no barcode at all.
        </p>
      )}
      {reason === 'not_found' && (
        // Capture at the point of unmet need. `product_search_miss` is already
        // the queue that decides what gets scored next, so this promise is one
        // we are structurally able to keep — the barcode rides along on the row.
        <div className="mx-auto mt-5 max-w-sm text-left">
          <SubscribeForm
            source="barcode_miss"
            context={barcode}
            variant="compact"
            cta="Tell me"
          />
          <p className="mt-2 text-center text-xs text-gray-500">
            We&apos;ll email you if this barcode gets scored.
          </p>
        </div>
      )}

      <div className="mt-5 flex flex-wrap justify-center gap-3">
        <AppStoreLink
          source="barcode_lookup_miss"
          ctaPosition="mid_article"
          trackingParams={{ page_type: 'barcode_check' }}
          className="inline-flex items-center rounded-control bg-ink px-6 py-3 text-cream transition-[color,background-color,border-color,text-decoration-color] hover:bg-ink/90 pressable"
        >
          Scan it in the app instead
        </AppStoreLink>
        <Link
          href="/products"
          className="inline-flex items-center rounded-xl border border-ink/20 px-6 py-3 text-ink transition-colors hover:border-forest/50"
        >
          Browse researched products
        </Link>
      </div>
    </div>
  )
}

function Result({ data }: { data: LookupResponse }) {
  const { product, estimate, context, curated, source } = data

  return (
    <div className="mt-6">
      {/* A researched page always beats a live estimate, so if we have one it
          goes above the estimate rather than below it. */}
      {curated && (
        <Link
          href={`/products/${curated.slug}`}
          className="mb-4 block rounded-2xl border border-forest/40 bg-forest/[0.06] p-4 transition-colors hover:border-forest"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-forest">
            We have a researched page for this
          </p>
          <p className="mt-1.5 font-semibold text-ink">
            {curated.name} — {curated.score}/100, evidence grade {curated.grade}
          </p>
          <p className="mt-0.5 text-sm text-gray-600">
            Sourced, graded and written by hand. Better than the estimate below →
          </p>
        </Link>
      )}

      {/* Estimate */}
      <div className="overflow-hidden rounded-2xl border border-ink/10 bg-white">
        <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:p-6">
          {product.image && (
            // Third-party CDN thumbnail already served small; routing it
            // through the Next optimiser would add per-image billing for no
            // visual gain.
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={product.image}
              alt={`${product.name} packaging`}
              width={88}
              height={88}
              loading="lazy"
              className="h-22 w-22 shrink-0 rounded-xl bg-cream object-contain"
            />
          )}
          <div className="min-w-0 flex-1">
            {product.brand && (
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                {product.brand}
              </p>
            )}
            <h2 className="font-display text-xl font-bold leading-snug text-ink">
              {product.name}
            </h2>
            {estimate.material && (
              <p className="mt-1 text-sm text-gray-600">
                Food-contact material:{' '}
                {estimate.material.materialSlug ? (
                  <Link href={`/materials/${estimate.material.materialSlug}`} className="text-forest underline">
                    {estimate.material.label}
                  </Link>
                ) : (
                  estimate.material.label
                )}
              </p>
            )}
          </div>
          <div className="shrink-0 text-center">
            <p className="font-display text-4xl font-bold leading-none tabular-nums text-ink">
              {estimate.score}
            </p>
            <p className="mt-1 text-2xs uppercase tracking-wider text-gray-500">/ 100</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-ink/10 px-5 py-4 sm:px-6">
          <span
            className="rounded-full px-3 py-1 text-xs font-bold text-white"
            style={{ backgroundColor: estimate.band.hex }}
          >
            {estimate.band.label} exposure
          </span>
          <span className="rounded-full border border-ink/15 px-3 py-1 text-xs font-semibold text-gray-700">
            Estimate · grade {estimate.grade}
          </span>
          <span className="rounded-full border border-ink/15 px-3 py-1 text-xs font-semibold tabular-nums text-gray-700">
            {estimate.confidence}% confidence
          </span>
        </div>

        <div className="border-t border-ink/10 px-5 py-4 sm:px-6">
          <ul className="space-y-2">
            {estimate.drivers.map((d) => (
              <li key={d} className="flex gap-2.5 text-sm leading-relaxed text-gray-700">
                <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-forest" />
                {d}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Food-quality context. Its own panel, its own attribution, explicitly
          outside our score — see the note in estimate.ts. */}
      {context.hasAny && (
        <div className="mt-4 rounded-2xl border border-ink/10 bg-cream/50 p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">
            Also on the label
          </p>
          <div className="mt-3 flex flex-wrap gap-2.5">
            {context.novaGroup && (
              <Fact label={`NOVA ${context.novaGroup}`} sub={context.novaLabel} />
            )}
            {context.nutriScore && <Fact label={`Nutri-Score ${context.nutriScore}`} />}
            {context.ecoScore && <Fact label={`Eco-Score ${context.ecoScore}`} />}
            {context.additivesCount !== undefined && (
              <Fact
                label={`${context.additivesCount} additive${context.additivesCount === 1 ? '' : 's'}`}
              />
            )}
          </div>
          <p className="mt-3.5 text-sm leading-relaxed text-gray-600">
            <strong className="text-ink">This is not part of our score.</strong>{' '}
            It is Open Food Facts&apos; own assessment of food quality, shown
            because it is useful and because you would otherwise have to open
            another app for it. Our number measures plastic contact and nothing
            else — folding nutrition into it would stop it meaning anything
            precise.
          </p>
        </div>
      )}

      {/* The gap. Same section every product page carries. */}
      <div className="mt-4 rounded-2xl border border-amber-300/60 bg-amber-50/70 p-5">
        <h3 className="font-display text-lg font-bold text-ink">What this estimate can&apos;t tell you</h3>
        <p className="mt-2 leading-relaxed text-gray-700">{estimate.unknown}</p>
        <p className="mt-3 text-sm text-gray-600">
          {estimate.gradeMeaning}{' '}
          <Link href="/methodology#evidence" className="text-forest underline">
            How evidence grades work →
          </Link>
        </p>
      </div>

      <p className="mt-4 text-xs leading-relaxed text-gray-500">
        Product data from{' '}
        <a
          href={source === 'open_beauty_facts' ? 'https://world.openbeautyfacts.org/' : 'https://world.openfoodfacts.org/'}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="underline"
        >
          {source === 'open_beauty_facts' ? 'Open Beauty Facts' : 'Open Food Facts'}
        </a>
        , licensed{' '}
        <a href="https://opendatacommons.org/licenses/odbl/1-0/" target="_blank" rel="noopener noreferrer nofollow" className="underline">
          ODbL
        </a>
        ; images{' '}
        <a href="https://creativecommons.org/licenses/by-sa/3.0/" target="_blank" rel="noopener noreferrer nofollow" className="underline">
          CC BY-SA
        </a>
        . Contributed by volunteers, so records can be incomplete or wrong. The
        exposure estimate is ours and is computed from that data.
      </p>
    </div>
  )
}

function Fact({ label, sub }: { label: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-ink/10 bg-white px-3.5 py-2">
      <p className="text-sm font-semibold text-ink">{label}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-500">{sub}</p>}
    </div>
  )
}
